import { useContext, useMemo, useState } from "react";
import {
  Paper,
  TableCell,
  TableContainer,
  TableBody,
  TableRow,
  Table,
  CircularProgress,
  Button,
  TextField,
  ThemeProvider,
  Select,
  MenuItem,
  useTheme,
  Box,
  Typography,
  Divider,
  Chip,
  FormHelperText,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { Close, Remove, Send, Add } from "@mui/icons-material";
import { InputTheme } from "../../themes/InputTheme";
import { ManagedAccountContext } from "../../context/ManagedAccountContext";
import { ModuleContext } from "../../context/ModuleContext";
import { CreateManagedAccountConfirm } from "./CreateManagedAccountConfirm";

const WSU_RED = "#A60F2D";

const labelCellSx = {
  fontWeight: 600,
  color: "#444",
  width: "35%",
  fontSize: "0.9rem",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

const selectSx = {
  width: "60%",
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: WSU_RED },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: WSU_RED },
};

export const CreateManagedAccount = () => {
  const outerTheme = useTheme();
  const { authState } = useOktaAuth();
  const { setManagedAccountModule } = useContext(ManagedAccountContext);
  const { setModule } = useContext(ModuleContext);

  const [state, setState] = useState({
    waiting: false,
    error: null,
    comment: "",
    name: "",
    displayName: "",
    resourceType: "Shared",
    manager: "",
    moderatedType: "Un-Moderated",
    phone: "",
    password: "",
    delegates: [],
  });

  const [proposedDelegate, setProposedDelegate] = useState("");

  const phoneRegex = /^(\+?\d{1,2}\s?)?(\(?\d{3}\)?[\s-]?)?\d{3}[\s-]?\d{4}$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{16}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleFieldChange = (field) => (e) =>
      setState((prev) => ({ ...prev, [field]: e.target.value }));

  const isPhoneInvalid = state.phone.length > 0 && !phoneRegex.test(state.phone);
  const isManagerInvalid = state.manager.length > 0 && !emailRegex.test(state.manager);
  const isPasswordInvalid = state.password.length > 0 && !passwordRegex.test(state.password);
  const isDelegateError = !!(
      proposedDelegate &&
      (!emailRegex.test(proposedDelegate) || state.delegates.includes(proposedDelegate))
  );

  const helperTextDelegates = useMemo(() => {
    if (!proposedDelegate) return "";
    if (!emailRegex.test(proposedDelegate)) return "Please provide a valid WSU email";
    if (state.delegates.includes(proposedDelegate)) return "User is already a delegate";
    return "";
  }, [proposedDelegate, state.delegates]);

  const isDelegateValid = useMemo(
      () => proposedDelegate && emailRegex.test(proposedDelegate) && !state.delegates.includes(proposedDelegate),
      [proposedDelegate, state.delegates]
  );

  const isFormValid = useMemo(() => {
    return (
        state.displayName.trim() &&
        state.name.trim() &&
        phoneRegex.test(state.phone.trim()) &&
        emailRegex.test(state.manager.trim()) &&
        passwordRegex.test(state.password) &&
        state.comment.trim()
    );
  }, [state]);

  const addDelegate = () => {
    if (isDelegateValid) {
      setState((prev) => ({
        ...prev,
        delegates: [...prev.delegates, proposedDelegate.trim()],
      }));
      setProposedDelegate("");
    }
  };

  const removeDelegate = (delegateToRemove) => {
    setState((prev) => ({
      ...prev,
      delegates: prev.delegates.filter((d) => d !== delegateToRemove),
    }));
  };

  const startCreateManagedAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true, error: null }));

    try {
      const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/managed-accounts/create`,
          {
            method: "POST",
            headers: {
              Authorization: authState.accessToken.accessToken,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              name: state.name.trim(),
              displayName: state.displayName.trim(),
              resourceType: state.resourceType,
              manager: state.manager.trim(),
              moderatedType: state.moderatedType,
              delegates: state.delegates,
              phone: state.phone.trim(),
              password: state.password,
              callerId: authState.accessToken.claims.sub,
              comment: state.comment.trim(),
            }),
          }
      );

      if (!res.ok) {
        const errText = await res.text();
        let errObj = {};
        try { errObj = JSON.parse(errText); } catch {
          errObj = { error: { message: errText || "Unknown error" } };
        }
        setState((prev) => ({
          ...prev,
          waiting: false,
          error: { message: errObj.error?.message || "Error creating account" },
        }));
        return;
      }

      setState((prev) => ({ ...prev, waiting: false }));
      setModule((prev) => ({
        ...prev,
        isVisible: true,
        isLog: true,
        element: (
            <CreateManagedAccountConfirm
                text={`Managed Account ${state.name} has been passed to CDS for create`}
            />
        ),
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  if (state.waiting) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  return (
      <ThemeProvider theme={InputTheme(outerTheme)}>
        <Box sx={{ width: "100%", position: "relative", left: "50%", right: "50%", ml: "-50vw", mr: "-50vw", display: "flex", justifyContent: "center", px: 2, py: 4 }}>
          <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 800, borderRadius: 2, overflow: "hidden" }}>

            {/* Error Banner */}
            {state.error && (
                <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Failed to create account
                  </Typography>
                  <Typography variant="body2">
                    {state.error.message || "Unknown error"}
                  </Typography>
                </Box>
            )}

            {/* ── Section: Account Details ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Account Details
              </Typography>
            </Box>

            <Table sx={{ minWidth: 500 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Name *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.name}
                        onChange={handleFieldChange("name")}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Display Name *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.displayName}
                        onChange={handleFieldChange("displayName")}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Password *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        type="password"
                        value={state.password}
                        onChange={handleFieldChange("password")}
                        error={isPasswordInvalid}
                        sx={fieldSx}
                    />
                    {isPasswordInvalid && (
                        <FormHelperText error>
                          Password must be exactly 16 characters and include uppercase, lowercase, a number, and a symbol.
                        </FormHelperText>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Contact & Organization ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Contact &amp; Organization
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Phone Number *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.phone}
                        onChange={handleFieldChange("phone")}
                        error={isPhoneInvalid}
                        sx={fieldSx}
                    />
                    {isPhoneInvalid && (
                        <FormHelperText error>Please enter a valid phone number</FormHelperText>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Manager *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.manager}
                        onChange={handleFieldChange("manager")}
                        error={isManagerInvalid}
                        placeholder="manager@wsu.edu"
                        sx={fieldSx}
                    />
                    {isManagerInvalid && (
                        <FormHelperText error>Please enter a valid email address</FormHelperText>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Mailbox Settings ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Mailbox Settings
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Resource Type</TableCell>
                  <TableCell>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={state.resourceType}
                        onChange={handleFieldChange("resourceType")}
                    >
                      <MenuItem value="Shared">Shared Mailbox</MenuItem>
                      <MenuItem value="Location">Location</MenuItem>
                      <MenuItem value="Resource">Resource</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Moderated Type</TableCell>
                  <TableCell>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={state.moderatedType}
                        onChange={handleFieldChange("moderatedType")}
                    >
                      <MenuItem value="Un-Moderated">Un-Moderated</MenuItem>
                      <MenuItem value="Moderated">Moderated</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Delegates ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Delegates
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Add Delegate</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box flex={1}>
                        <TextField
                            fullWidth
                            size="small"
                            value={proposedDelegate}
                            onChange={(e) => setProposedDelegate(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && isDelegateValid) addDelegate(); }}
                            error={isDelegateError}
                            placeholder="Enter delegate email"
                            sx={fieldSx}
                        />
                        {helperTextDelegates && (
                            <FormHelperText error>{helperTextDelegates}</FormHelperText>
                        )}
                      </Box>
                      <Button
                          variant="contained"
                          onClick={addDelegate}
                          disabled={!isDelegateValid}
                          sx={{
                            backgroundColor: WSU_RED,
                            color: "white",
                            minWidth: 44,
                            px: 1.5,
                            mt: 0.25,
                            "&:hover": { backgroundColor: "#8a0c25" },
                            "&.Mui-disabled": { backgroundColor: "#ddd" },
                          }}
                      >
                        <Add />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {state.delegates.length > 0 ? (
                <Box sx={{ px: 3, py: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {state.delegates.map((delegate) => (
                      <Chip
                          key={delegate}
                          label={delegate}
                          onDelete={() => removeDelegate(delegate)}
                          deleteIcon={<Remove fontSize="small" />}
                          sx={{
                            backgroundColor: "#fdf0f2",
                            border: `1px solid ${WSU_RED}`,
                            color: WSU_RED,
                            fontWeight: 500,
                            "& .MuiChip-deleteIcon": {
                              color: WSU_RED,
                              "&:hover": { color: "#8a0c25" },
                            },
                          }}
                      />
                  ))}
                </Box>
            ) : (
                <Box sx={{ px: 3, py: 1.5 }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No delegates added
                  </Typography>
                </Box>
            )}

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Justification ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Justification
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Comment *</TableCell>
                  <TableCell>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        value={state.comment}
                        onChange={handleFieldChange("comment")}
                        placeholder="Provide a reason for creating this account"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Submit / Cancel ── */}
            <Box sx={{ px: 3, pt: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                  variant="outlined"
                  endIcon={<Close />}
                  onClick={() => setManagedAccountModule((prev) => ({ ...prev, isOpen: false }))}
                  sx={{
                    borderColor: "#ccc",
                    color: "#666",
                    "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" },
                  }}
              >
                Cancel
              </Button>
              <Button
                  variant="contained"
                  endIcon={<Send />}
                  onClick={startCreateManagedAccount}
                  disabled={!isFormValid}
                  sx={{
                    backgroundColor: WSU_RED,
                    color: "white",
                    px: 4,
                    py: 1.25,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    "&:hover": { backgroundColor: "#8a0c25" },
                    "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
                  }}
              >
                Create Account
              </Button>
            </Box>

          </TableContainer>
        </Box>
      </ThemeProvider>
  );
};